import { useApolloClient } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  createUpdateGQLFragmentGenerator,
  Details,
  type DetailsProps,
  EmptyStation,
  type FormActionData,
  formatDateTime,
  generateArrayMutations,
  getFormDiff,
  InfoPanel,
  MessageBar,
  type Nullable,
  type ObjectSchemaDefinition,
  Paragraph,
  Section,
  SingleLineTextField,
  type StationMessage,
} from '@axinom/mosaic-ui';
import * as EmailValidator from 'email-validator';
import { Field, useFormikContext } from 'formik';
import gql from 'graphql-tag';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import {
  type EmailWhitelistPattern,
  EmailWhitelistPatternDocument,
  type EmailWhitelistPatternQuery,
  type EmailWhitelistPatternRole,
  type Mutation,
  type MutationCreateEmailWhitelistPatternRoleArgs,
  type MutationDeleteEmailWhitelistPatternRoleArgs,
  type MutationUpdateEmailWhitelistPatternArgs,
  type UpdateEmailWhitelistPatternMutationVariables,
  useDeleteEmailWhitelistPatternMutation,
  useEmailWhitelistPatternQuery,
} from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { apolloClient } from '../../../../modules/apolloClient';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import type { UserRoleAssignment } from './RoleSelectionExplorer';
import { UserRoleSelectField } from './UserRoleSelectField';

type FormData = Nullable<
  UpdateEmailWhitelistPatternMutationVariables['input']['patch']
> &
  Partial<EmailWhitelistPatternQuery['emailWhitelistPattern']> & {
    userRoles: UserRoleAssignment[];
  };

type RoleAssignment = Pick<
  EmailWhitelistPatternRole,
  'id' | 'userRoleId' | 'userRoleName' | 'userRoleDescription'
>;

const wildcardReplacement = 'ax';
const emailWhitelistPatternSchema = Yup.object<
  ObjectSchemaDefinition<FormData>
>({
  emailPattern: Yup.string()
    .label('Email Whitelist Pattern')
    .required()
    .max(200)
    .test(
      'email-whitelist-pattern',
      'Email Whitelist Pattern is not valid.',
      function (pattern: string | undefined) {
        if (pattern !== undefined) {
          if (pattern === '*') {
            return true;
          }

          // Prevent adjacent repetition of wildcard character '*'
          if (pattern.indexOf('**') !== -1) {
            return false;
          }

          // Replace wildcard character with a fixed replacement string, and validate the resulting email
          const email = pattern.replaceAll('*', wildcardReplacement);
          return EmailValidator.validate(email);
        }
        return false;
      },
    ),
  userRoles: Yup.array(),
});

const generateUpdateGQLFragment = createUpdateGQLFragmentGenerator<Mutation>();

export const EmailWhitelistPatternDetail: React.FC = () => {
  const { envId, emailWhitelistPatternId } = useParams<{
    envId: string;
    emailWhitelistPatternId: string;
  }>();

  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);
  const apolloClient = useApolloClient();

  // Check the status of id-service
  useEffect(() => {
    (async () => {
      const statusMessage = await getManagedServiceAvailabilityMessage(
        ManagedServiceId.ID_SERVICE,
        envId,
      );
      setServiceStatusMessage(statusMessage);
    })();
  }, [envId]);

  // Query email whitelist pattern data
  const { loading, data, error } = useEmailWhitelistPatternQuery({
    client: apolloClient,
    variables: { id: emailWhitelistPatternId },
    fetchPolicy: 'no-cache',
  });

  const { initialRoles } = useInitialRoleAssignments(
    data?.emailWhitelistPatternRoles?.nodes,
  );

  const { actions } = useActions(envId, emailWhitelistPatternId);

  const onSubmit = useCallback(
    async (
      formData: FormData,
      initialData: DetailsProps<FormData>['initialData'],
    ): Promise<void> => {
      // Calculate the diff on the native properties of the entity
      const patch = createUpdateDto(formData, initialData.data);
      let emailWhitelistPatternUpdateMutation = '';
      if (Object.keys(patch).length > 0) {
        emailWhitelistPatternUpdateMutation =
          generateUpdateGQLFragment<MutationUpdateEmailWhitelistPatternArgs>(
            'updateEmailWhitelistPattern',
            {
              input: { id: emailWhitelistPatternId, patch: patch },
            },
          );
      }

      const userRoleMutations = generateArrayMutations({
        current: formData.userRoles,
        original: initialData.data?.userRoles,
        generateCreateMutation: (userRole) =>
          generateUpdateGQLFragment<MutationCreateEmailWhitelistPatternRoleArgs>(
            'createEmailWhitelistPatternRole',
            {
              input: {
                emailWhitelistPatternRole: {
                  emailWhitelistPatternId,
                  userRoleId: userRole.id,
                  environmentId: envId,
                },
              },
            },
          ),
        generateDeleteMutation: (userRole) =>
          generateUpdateGQLFragment<MutationDeleteEmailWhitelistPatternRoleArgs>(
            'deleteEmailWhitelistPatternRole',
            {
              input: { id: userRole.assignmentId },
            },
          ),
        prefix: 'emailWhitelistPatternRole',
      });

      const GqlDoc = gql`mutation UpdateEmailWhitelistPatternRole {
        ${emailWhitelistPatternUpdateMutation}
        ${userRoleMutations}
      }`;

      // sending the mutation to the backend
      await apolloClient.mutate({ mutation: GqlDoc });
    },
    [emailWhitelistPatternId, envId, apolloClient],
  );

  const [message] = useState<StationMessage>({
    title:
      "Email whitelist pattern supports the '*' (asterisk) wildcard character (valid examples: name@domain.com, *@domain.com, department@*.domain.com).",
    type: 'info',
  });

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation
          title="Email Whitelist Pattern"
          subtitle="Email Whitelist Pattern Details"
        >
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <Details<FormData>
          defaultTitle="Email Whitelist Pattern"
          titleProperty="emailPattern"
          subtitle="Email Whitelist Pattern Details"
          actions={actions}
          validationSchema={emailWhitelistPatternSchema}
          initialData={{
            data: {
              ...data?.emailWhitelistPattern,
              userRoles: initialRoles ?? [],
            },
            loading,
            error: error?.message,
          }}
          saveData={onSubmit}
          infoPanel={<Panel />}
          stationMessage={message}
        >
          <Form environmentId={envId} initialRoles={initialRoles} />
        </Details>
      )}
    </>
  );
};

const Panel: React.FC = () => {
  const { values } = useFormikContext<EmailWhitelistPattern>();

  return (
    <InfoPanel>
      <Section>
        <Paragraph title="Email Whitelist Pattern ID">{values.id}</Paragraph>
        <Paragraph title="Created">
          {formatDateTime(values.createdAt)} by {values.createdBy}
        </Paragraph>
        <Paragraph title="Last Modified">
          {formatDateTime(values.updatedAt)} by {values.updatedBy}
        </Paragraph>
      </Section>
      <Section>
        <Paragraph title="Tenant ID">{values.tenantId}</Paragraph>
        <Paragraph title="Environment ID">{values.environmentId}</Paragraph>
      </Section>
    </InfoPanel>
  );
};

const Form: React.FC<{
  environmentId: string;
  initialRoles: UserRoleAssignment[];
}> = ({ environmentId, initialRoles }) => {
  return (
    <>
      <Field
        name="emailPattern"
        label="Email Whitelist Pattern"
        as={SingleLineTextField}
      />
      <Field
        name="userRoles"
        label="Assigned Roles"
        environmentId={environmentId}
        initialRoles={initialRoles}
        as={UserRoleSelectField}
      />
    </>
  );
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function useActions(envId: string, emailWhitelistPatternId: string) {
  const apolloClient = useApolloClient();
  const history = useHistory();

  const [deleteEmailWhitelistPattern] = useDeleteEmailWhitelistPatternMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const actions: FormActionData[] = [
    {
      label: 'Delete',
      confirmationMode: 'Simple',
      onActionSelected: async () => {
        await deleteEmailWhitelistPattern({
          variables: {
            input: {
              id: emailWhitelistPatternId,
            },
          },
          fetchPolicy: 'no-cache',
        });

        history.push(
          `/env/${envId}/services/${ManagedServiceId.ID_SERVICE}/config/emailWhitelistPatterns`,
        );
      },
    },
  ];

  return { actions } as const;
}

const createUpdateDto = (
  currentValues: FormData,
  initialValues?: FormData | null,
): Omit<FormData, 'userRoles'> => {
  const updateData = getFormDiff(currentValues, initialValues);
  delete updateData['userRoles'];
  return updateData;
};

/**
 * Formats role assignments for use in the form
 * @param initialAssignments current role assignments on server
 */
const useInitialRoleAssignments = (
  initialAssignments?: RoleAssignment[],
): {
  readonly initialRoles: UserRoleAssignment[];
} => {
  const [initialRoles, setInitialRoles] = useState<UserRoleAssignment[]>([]);
  useEffect(() => {
    if (initialAssignments !== undefined) {
      setInitialRoles(
        initialAssignments
          .map((emailWhitelistPatternRole) => {
            return {
              assignmentId: emailWhitelistPatternRole.id,
              id: emailWhitelistPatternRole.userRoleId,
              name: emailWhitelistPatternRole.userRoleName ?? '',
              description: emailWhitelistPatternRole.userRoleDescription ?? '',
            };
          })
          .sort((a, b) => {
            return a.name > b.name ? 1 : -1;
          }),
      );
    }
  }, [initialAssignments]);
  return { initialRoles } as const;
};

export const EmailWhitelistPatternDetailCrumb: BreadcrumbResolver = ({
  emailWhitelistPatternId,
}) => {
  return async (): Promise<string> => {
    const response = await apolloClient().query<EmailWhitelistPatternQuery>({
      query: EmailWhitelistPatternDocument,
      variables: { id: emailWhitelistPatternId },
      errorPolicy: 'ignore',
    });
    return (
      response.data.emailWhitelistPattern?.emailPattern ||
      'Email Whitelist Pattern'
    );
  };
};
